/* --------------------------- Design states start -------------------------- */
import { FormControl, FormHelperText, TextField } from '@mui/material'
/* ---------------------------- Design states end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useEffect, useState } from "react";
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  id?: string
  value: string
  label: string
  labelWidth?: number
  onChange: (value: string) => void
  validation?: boolean
  errorMessage?: string
  disabled?: boolean
  required?: boolean
  multiline?: boolean
  showError?: boolean
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function EmailInput(props: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  const {
    id,
    label,
    value = '',
    onChange,
    disabled = false,
    required = false,
    errorMessage = '',
    validation,
    multiline,
    showError = true,
  } = props
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  const [error, setError] = useState<boolean>(showError && validation === true && value === '')
  /* ----------------------------- Flag states end ---------------------------- */

  const isValidEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  }

  /* ------------------------------ Effects start ----------------------------- */
  useEffect(() => {
    if (showError) {
      validation === true && value === '' ? setError(true) : setError(false)
    } else {
      setError(false)
    }
  }, [showError, value, validation])
  /* ------------------------------- Effects end ------------------------------ */

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <FormControl fullWidth variant="outlined">
      <TextField
        id={id || label}
        variant="outlined"
        multiline={multiline}
        rows={multiline ? 4 : 1}
        label={label}
        value={value || ''}
        onChange={e => {
          e.currentTarget.value !== '' || validation !== true
            ? setError(false)
            : setError(true)
          onChange(e.currentTarget.value)
        }}
        autoComplete="text"
        required={required}
        disabled={disabled}
        error={error || (value !== '' && !isValidEmail(value))}
        aria-describedby="text-input"
        inputProps={{
          'aria-label': 'weight',
        }}
      />
      {error && (
        <FormHelperText error variant="outlined">
          {errorMessage}
        </FormHelperText>
      )}
    </FormControl>
  )
}
